import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Send } from "lucide-react";
import { submitRequisition } from "@/lib/api";
import { REQUISITION_BOOKS, REQUISITION_FORMATS } from "@/data/secrets";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

const Chip = ({ active, children, ...rest }) => (
  <button
    type="button"
    aria-pressed={active}
    className={`rounded-[9px] border px-3 py-2 text-left font-mono text-[11px] uppercase tracking-[0.1em] transition-colors duration-150 ${
      active
        ? "border-[hsl(174_62%_30%)] bg-[hsl(220_18%_10%)] text-[hsl(38_38%_94%)]"
        : "border-[hsl(36_18%_72%)] bg-white text-[hsl(220_14%_30%)] hover:bg-[hsl(38_38%_96%)]"
    }`}
    {...rest}
  >
    {children}
  </button>
);

/** Advance requisition: a direct request to the author for an unreleased book. */
export const RequisitionForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    book: REQUISITION_BOOKS[5],
    book_format: REQUISITION_FORMATS[0],
    quantity: 1,
    region: "",
    message: ""
  });
  const [honey, setHoney] = useState("");
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [reference, setReference] = useState(null);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = "Required.";
    if (!EMAIL_RE.test(form.email.trim()))
      next.email = "That address does not resolve.";
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setState("loading");
    try {
      const data = await submitRequisition({
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        quantity: Number(form.quantity) || 1,
        region: form.region.trim() || null,
        message: form.message.trim() || null,
        signal_key: honey
      });
      setReference(data.reference);
      setState("done");
      toast.success("Requisition logged", { description: data.message });
    } catch (err) {
      setState("idle");
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Transmission failed. Please try again in a moment.";
      toast.error("Could not log the requisition", { description: msg });
      setErrors({ form: msg });
    }
  };

  if (state === "done") {
    return (
      <div
        className="rounded-[var(--radius)] border border-[hsl(36_20%_74%)] bg-[hsl(38_38%_96%)] p-5"
        data-testid="requisition-success"
        role="status"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[hsl(174_62%_30%)] text-white">
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[hsl(220_12%_40%)]">
              Requisition logged · reference{" "}
              <span
                className="text-[hsl(var(--ink))]"
                data-testid="requisition-reference"
              >
                {reference}
              </span>
            </p>
            <p className="mt-2 text-sm leading-relaxed text-[hsl(var(--ink))]">
              This went straight to the author — no shop, no middle layer, no
              payment taken. He will reply to you directly about the copy,
              pricing and when it can actually ship.
            </p>
            <p className="mt-2 text-xs text-[hsl(220_12%_42%)]">
              A copy of this requisition has been sent to your address. Keep the
              reference — it is the only record of this request that exists.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const labelCls =
    "block font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(220_12%_38%)]";
  const inputCls =
    "mt-2 h-11 w-full rounded-[10px] border border-[hsl(36_18%_70%)] bg-white px-3.5 text-[15px] text-[hsl(var(--ink))] placeholder:text-[hsl(220_8%_58%)] transition-colors duration-150 focus:border-[hsl(174_62%_34%)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(174_62%_34%)]";
  const errCls =
    "mt-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[hsl(2_72%_40%)]";

  return (
    <form onSubmit={submit} noValidate className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="req-name" className={labelCls}>
            Name for the inscription
          </label>
          <input
            id="req-name"
            data-testid="requisition-name-input"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Who is it for?"
            className={inputCls}
          />
          {errors.name && <p className={errCls}>{errors.name}</p>}
        </div>

        <div>
          <label htmlFor="req-email" className={labelCls}>
            Reply address
          </label>
          <input
            id="req-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            data-testid="requisition-email-input"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="you@somewhere.real"
            className={inputCls}
          />
          {errors.email && <p className={errCls}>{errors.email}</p>}
        </div>
      </div>

      <div>
        <span className={labelCls}>Which file</span>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          {REQUISITION_BOOKS.map((b) => (
            <Chip
              key={b}
              active={form.book === b}
              onClick={() => set("book", b)}
              data-testid={`requisition-book-${b.split(" ")[1]?.toLowerCase() || "series"}`}
            >
              {b}
            </Chip>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-[1fr_7rem]">
        <div>
          <span className={labelCls}>Preferred form</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {REQUISITION_FORMATS.map((f) => (
              <Chip
                key={f}
                active={form.book_format === f}
                onClick={() => set("book_format", f)}
              >
                {f}
              </Chip>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="req-qty" className={labelCls}>
            Copies
          </label>
          <input
            id="req-qty"
            type="number"
            min={1}
            max={20}
            data-testid="requisition-quantity-input"
            value={form.quantity}
            onChange={(e) => set("quantity", e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <label htmlFor="req-region" className={labelCls}>
          Where it would ship (optional)
        </label>
        <input
          id="req-region"
          data-testid="requisition-region-input"
          value={form.region}
          onChange={(e) => set("region", e.target.value)}
          placeholder="Country or region"
          className={inputCls}
        />
      </div>

      <div>
        <label htmlFor="req-message" className={labelCls}>
          Message to the author (optional)
        </label>
        <textarea
          id="req-message"
          rows={4}
          data-testid="requisition-message-input"
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          placeholder="Anything you want on the inscription, or anything you want to ask."
          className="mt-2 w-full resize-y rounded-[10px] border border-[hsl(36_18%_70%)] bg-white px-3.5 py-3 text-[15px] leading-relaxed text-[hsl(var(--ink))] placeholder:text-[hsl(220_8%_58%)] transition-colors duration-150 focus:border-[hsl(174_62%_34%)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(174_62%_34%)]"
        />
      </div>

      <input
        type="text"
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        value={honey}
        onChange={(e) => setHoney(e.target.value)}
        style={{ position: "absolute", left: "-9999px" }}
      />

      <div className="flex flex-col gap-3 border-t border-[hsl(36_16%_80%)] pt-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-xs text-xs leading-relaxed text-[hsl(220_10%_42%)]">
          No payment is taken here and nothing is charged. This is a direct
          request to the author.
        </p>
        <button
          type="submit"
          disabled={state === "loading"}
          data-testid="requisition-submit-button"
          className="inline-flex h-12 items-center justify-center gap-2 rounded-[10px] bg-[hsl(220_18%_10%)] px-6 font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(38_38%_94%)] shadow-[0_10px_30px_rgba(0,0,0,0.25)] transition-colors duration-150 hover:bg-[hsl(174_62%_26%)] disabled:opacity-60"
        >
          {state === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Transmitting
            </>
          ) : (
            <>
              <Send className="h-3.5 w-3.5" />
              Send the requisition
            </>
          )}
        </button>
      </div>

      {errors.form && (
        <p className={errCls} role="alert" data-testid="requisition-error">
          {errors.form}
        </p>
      )}
    </form>
  );
};
